import { EventClickArea, EventClickAttributes } from '../types';
import { logger } from '@tuic/logger';
import { trackEvent } from './event-tracker';
import { normalizeTrackingValue } from '../utils';

const getAttribute = (
  el: Element,
  attr: EventClickAttributes,
): string | undefined => {
  return el.closest(`[${attr}]`)?.getAttribute(attr) ?? undefined;
};

const getPositionKey = (position: string | undefined): string | undefined => {
  return position ? `pos${String(+position + 1).padStart(2, '0')}` : undefined;
};

export const handleClickAttributionClick = (event: MouseEvent | TouchEvent) => {
  let element = event.target;

  if (!(element instanceof Element)) {
    return;
  }

  let clickLabel = getAttribute(element, EventClickAttributes.Label);

  let clickElement = getAttribute(element, EventClickAttributes.Element);

  if (!clickLabel && !clickElement) {
    return;
  }

  // graceful error handling for general click tracking
  if (!clickLabel || !clickElement) {
    logger.warn(
      `Tracking: click event failed. data-track-click-label is ${clickLabel} and data-track-click-element is ${clickElement}`,
    );

    return;
  }

  let clickCategory = getAttribute(element, EventClickAttributes.Category);
  let clickArea = getAttribute(element, EventClickAttributes.Area);
  let clickModule = getAttribute(element, EventClickAttributes.Module);
  let clickModuleType = getAttribute(element, EventClickAttributes.ModuleType);
  let clickPosition = getAttribute(element, EventClickAttributes.Position);
  let clickDialog = getAttribute(element, EventClickAttributes.Dialog);

  // let delay: boolean = false;
  // const delayElement = event.target.closest(`[${EventClickAttributes.Delay}]`);
  // if (
  //   delayElement &&
  //   delayElement.getAttribute(EventClickAttributes.Delay) === 'true' &&
  //   delayElement.getAttribute('target') !== '_blank'
  // ) {
  //   delay = true;
  // }

  // graceful error handling for dialog click tracking
  if (clickDialog && clickArea !== EventClickArea.Dialog) {
    logger.warn(
      `Tracking: layer-click event failed. data-track-dialog is ${clickDialog}, but data-track-click-area found in parents is ${clickArea} (should be "layer")`,
    );

    return;
  }

  if (!clickDialog && clickArea === EventClickArea.Dialog) {
    logger.warn(
      `Tracking: layer-click event failed. data-track-click-area="${clickArea}" found in click tracking, but data-track-dialog is ${clickDialog} (not found in parent nodes)`,
    );

    return;
  }

  // graceful error handling for module click tracking
  if (clickModule && clickArea !== EventClickArea.Content) {
    logger.warn(
      `Tracking: module-click event failed. data-track-module is ${clickModule}, but data-track-click-area found in parents is ${clickArea} (should be "module")`,
    );

    return;
  }

  let eventClickLabel = [
    clickArea,
    clickElement,
    normalizeTrackingValue(clickCategory),
    getPositionKey(clickPosition),
    normalizeTrackingValue(clickLabel),
  ]
    .filter(elem => !!elem)
    .join('.');

  if (clickDialog) {
    trackEvent({
      event_name: 'layer-click',
      click_label: eventClickLabel,
      layer_name: normalizeTrackingValue(clickDialog) ?? '',
    });

    return;
  }

  if (clickModule) {
    let moduleType = clickModuleType
      ? [normalizeTrackingValue(clickModuleType), getPositionKey(clickPosition)]
          .filter(elem => !!elem)
          .join('.')
      : 'main';

    trackEvent({
      event_name: 'module-click',
      module_item: eventClickLabel,
      module_name: normalizeTrackingValue(clickModule) ?? '',
      module_type: moduleType,
    });

    return;
  }

  trackEvent({ event_name: 'click', click_label: eventClickLabel });
};
